import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or get visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('visitor_id', visitorId);
  }
  return visitorId;
};

// Check if user is admin (from context or localStorage)
const checkIsAdmin = (isAdminFromContext) => {
  // Check context first
  if (isAdminFromContext) return true;
  
  // Also check localStorage token
  const token = localStorage.getItem('admin_token');
  if (token) return true;
  
  // Check if on admin page
  if (window.location.pathname.startsWith('/admin')) return true;
  
  return false;
};

// Detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'Tablette';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'Mobile';
  }
  return 'Desktop';
};

// Detect traffic source
const getTrafficSource = () => {
  const referrer = document.referrer;
  const urlParams = new URLSearchParams(window.location.search);
  
  // Check UTM parameters first
  const utmSource = urlParams.get('utm_source');
  if (utmSource) return utmSource;
  
  if (!referrer) return 'Direct';
  
  try {
    const refDomain = new URL(referrer).hostname.toLowerCase();
    
    if (refDomain.includes('google')) return 'Google';
    if (refDomain.includes('facebook') || refDomain.includes('fb.com')) return 'Facebook';
    if (refDomain.includes('instagram')) return 'Instagram';
    if (refDomain.includes('bing')) return 'Bing';
    if (refDomain.includes('yahoo')) return 'Yahoo';
    if (refDomain.includes('twitter') || refDomain.includes('t.co')) return 'Twitter/X';
    if (refDomain.includes('linkedin')) return 'LinkedIn';
    if (refDomain.includes('pinterest')) return 'Pinterest';
    if (refDomain.includes('tiktok')) return 'TikTok';
    if (refDomain.includes('youtube')) return 'YouTube';
    
    return refDomain;
  } catch {
    return 'Direct';
  }
};

export function useAnalytics() {
  const location = useLocation();
  const { isAdmin } = useAuth();

  useEffect(() => {
    // Don't track admin visits (check both context and localStorage)
    if (checkIsAdmin(isAdmin)) {
      console.debug('Analytics: Admin visit not tracked');
      return;
    }
    
    // Check if user has accepted cookies
    const consent = localStorage.getItem('cookie-consent');
    if (consent !== 'accepted') return;

    const trackPageView = async () => {
      try {
        const visitorId = getVisitorId();
        const params = new URLSearchParams({
          page: location.pathname,
          visitor_id: visitorId,
          user_agent: navigator.userAgent,
          referer: document.referrer || '',
          device: getDeviceType(),
          source: getTrafficSource()
        });

        await fetch(`${API_URL}/api/analytics/pageview?${params}`, {
          method: 'POST'
        });
      } catch (error) {
        console.debug('Analytics error:', error);
      }
    };

    trackPageView();
  }, [location.pathname, isAdmin]);
}

export function AnalyticsProvider({ children }) {
  useAnalytics();
  return children;
}
